import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@beyou/environments/environment';
import { BaseApiService } from '../base-api.service';
import { PatientDetailsDto } from '../../dto/patients/patient-details.dto';
import { PatientDto } from '../../dto/patients/patient.dto';
import { PatientMapper } from '../../mappers/patients/patient.mapper';
import { PatientDetails } from '../../models/patients/patient-details.model';
import { Patient, PatientListQuery } from '../../models/patients/patient.model';
import { PagedResponse } from '../../dto/paged-response';
import { toPagedQueryParams } from '../../models/paged-query.model';

@Injectable({
  providedIn: 'root',
})
export class PatientsApiService extends BaseApiService {

  constructor(http: HttpClient) {
    super(http);
  }

  getAll(practiceId: number, practitionerId: number): Observable<Patient[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/practice/${practiceId}/practitioners/${practitionerId}/patients`;
    return this.getAllPagedData<PatientDto>(apiUrl).pipe(
      map((response) => {
        return response.map(item => {
          return PatientMapper.toModel(item);
        })
      })
    );
  }

  /**
   * One page of a practitioner's patients, for a paginated list. `getAll()` walks every page
   * instead and stays the way `PatientService` fills the user store.
   */
  list(practiceId: number, practitionerId: number, query: PatientListQuery = {}): Observable<PagedResponse<Patient>> {
    const apiUrl = `${environment.adminApiBaseUrl}/practice/${practiceId}/practitioners/${practitionerId}/patients`;
    const params = { ...toPagedQueryParams(query), ...(query.excludePracticeOwner ? { exclude_practice_owner: 'true' } : {}) };
    return this.http.get<PagedResponse<PatientDto>>(apiUrl, { params }).pipe(
      map((page) => ({
        ...page,
        data: page.data.map((item) => PatientMapper.toModel(item))
      }))
    );
  }

  public async get(practiceId: number, practitionerId: number, patientId: number): Promise<PatientDetails> {
    const apiUrl = `${environment.adminApiBaseUrl}/practice/${practiceId}/practitioners/${practitionerId}/patients/${patientId}`;
    return await firstValueFrom(this.http.get<PatientDetailsDto>(apiUrl).pipe(
      map((response) => {
        return PatientMapper.toDetailedModel(response);
      })
    ));
  }
}
